import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  getWarningsFailure,
  getWarningsSuccess,
  saveWarningSuccess,
  saveWarningFailure,
  updateWarningSuccess,
  updateWarningFailure,
  dismissWarningSuccess,
  dismissWarningFailure,
  recordScheduleReviewSuccess,
  recordScheduleReviewFailure,
  acknowledgeWarningSuccess,
  acknowledgeWarningFailure,
  monitorWarningSuccess,
  monitorWarningFailure,
  resolveWarningSuccess,
  resolveWarningFailure,
} from "./actions";
import {
  GET_WARNINGS,
  SAVE_WARNING,
  UPDATE_WARNING,
  DISMISS_WARNING,
  RECORD_SCHEDULE_REVIEW,
  ACKNOWLEDGE_WARNING,
  MONITOR_WARNING,
  RESOLVE_WARNING,
} from "./constants";
import { capitalizeFirstLetter } from "../../utils/helpers";

export function* getWarningsSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  let query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(
      api(token).get,
      `/api/care-plan-warning-alert?${query}`,
    );
    yield put(getWarningsSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getWarningsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching network!"));
  }
}

export function* saveWarningSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/care-plan-warning-alert/add`,
      payload,
    );
    yield put(saveWarningSuccess(data));
    yield put(
      successAlert(
        `${capitalizeFirstLetter(payload.type)} saved successfully!`,
      ),
    );
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(saveWarningFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving warning!"));
  }
}

export function* updateWarningSaga({
  payload = { cancelToken: null },
  onUpdate,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { type, id, formData } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/care-plan-warning-alert/${type}/resident/${id}`,
      formData,
    );
    yield put(updateWarningSuccess(data));
    yield put(successAlert("Warning updated successfully!"));
    typeof onUpdate === "function" && onUpdate();
  } catch (e) {
    yield put(updateWarningFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating warning!"));
  }
}

export function* dismissWarningSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { uuid, dismiss_type, dismiss_reason } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/care-plan-warning-alert/dismiss/${uuid}`,
      { dismiss_type, dismiss_reason },
    );
    yield put(dismissWarningSuccess(data));
    yield put(successAlert("Alert dismissed."));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(dismissWarningFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while dismissing alert!"));
  }
}

export function* getWarningsFlow() {
  yield takeEvery(GET_WARNINGS, getWarningsSaga);
}
export function* saveWarningFlow() {
  yield takeEvery(SAVE_WARNING, saveWarningSaga);
}
export function* updateWarningFlow() {
  yield takeEvery(UPDATE_WARNING, updateWarningSaga);
}

export function* dismissWarningFlow() {
  yield takeEvery(DISMISS_WARNING, dismissWarningSaga);
}

export function* recordScheduleReviewSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { uuid, schedule_uuid, action, resolve } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/care-plan-warning-alert/schedule-review/${uuid}`,
      { schedule_uuid, action, resolve: Boolean(resolve) },
    );
    yield put(recordScheduleReviewSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(recordScheduleReviewFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error recording schedule review!"));
  }
}

export function* recordScheduleReviewFlow() {
  yield takeEvery(RECORD_SCHEDULE_REVIEW, recordScheduleReviewSaga);
}

// "Looks fine" — acknowledge the alert (reviewed, no action needed).
export function* acknowledgeWarningSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { uuid } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/care-plan-warning-alert/acknowledge/${uuid}`,
      {},
    );
    yield put(acknowledgeWarningSuccess(data));
    yield put(successAlert("Marked as reviewed."));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(acknowledgeWarningFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while marking reviewed!"));
  }
}

export function* acknowledgeWarningFlow() {
  yield takeEvery(ACKNOWLEDGE_WARNING, acknowledgeWarningSaga);
}

// "Check later" — park the alert for follow-up with a review date.
export function* monitorWarningSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { uuid, review_due_at, reason, note } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/care-plan-warning-alert/monitor/${uuid}`,
      { review_due_at, reason: reason || null, note: note || null },
    );
    yield put(monitorWarningSuccess(data));
    yield put(successAlert("Set to monitor — we'll bring it back for review."));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(monitorWarningFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while setting alert to monitor!"));
  }
}

export function* monitorWarningFlow() {
  yield takeEvery(MONITOR_WARNING, monitorWarningSaga);
}

// "Actioned" — resolve the alert, recording the action taken + note.
export function* resolveWarningSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { uuid, action, note } = payload || {};
  try {
    const { data } = yield call(
      api(token).post,
      `/api/care-plan-warning-alert/resolve/${uuid}`,
      { action: action || null, note: note || null },
    );
    yield put(resolveWarningSuccess(data));
    yield put(successAlert("Marked as actioned."));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(resolveWarningFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while marking actioned!"));
  }
}

export function* resolveWarningFlow() {
  yield takeEvery(RESOLVE_WARNING, resolveWarningSaga);
}

export default function* ResidentWarningsSaga() {
  yield all([
    getWarningsFlow(),
    saveWarningFlow(),
    updateWarningFlow(),
    dismissWarningFlow(),
    recordScheduleReviewFlow(),
    acknowledgeWarningFlow(),
    monitorWarningFlow(),
    resolveWarningFlow(),
  ]);
}
